import { signal } from "@preact/signals-core";

const HEARTBEAT_INTERVAL = 30000;
const TIME_TO_LIVE = 90000;
const SWEEP_INTERVAL = 10000;
const JOIN_ANSWER_MIN = 150;
const JOIN_ANSWER_MAX = 600;

type Timer = ReturnType<typeof globalThis.setTimeout>;
type IntervalTimer = ReturnType<typeof globalThis.setInterval>;
type PresenceEvent = "join" | "here" | "leave";
type Entry = { accountId: string; client: string; lastSeen: number };

// A campaign-wide roster kept only by the client: no server-side table, so
// each board tracks who else is heard from on [campaign, :maps]. `listen`
// hears the roster without joining it: nothing is sent and the viewer is not
// in it.
export function createPresence({
	self,
	send,
	listen = false,
	now = Date.now,
	setTimeout = globalThis.setTimeout,
	clearTimeout = globalThis.clearTimeout,
	setInterval = globalThis.setInterval,
	clearInterval = globalThis.clearInterval,
}: {
	self: { accountId: string; client: string };
	send: (payload: { join: boolean }) => void;
	listen?: boolean;
	now?: () => number;
	setTimeout?: (fn: () => void, ms: number) => Timer;
	clearTimeout?: (timer: Timer) => void;
	setInterval?: (fn: () => void, ms: number) => IntervalTimer;
	clearInterval?: (timer: IntervalTimer) => void;
}) {
	const entries = new Map<string, Entry>();
	const present = signal(new Set<string>(listen ? [] : [self.accountId]));

	let connected = false;
	let heartbeat: IntervalTimer | null = null;
	let joinAnswer: Timer | null = null;
	let hiddenAt: number | null = null;
	let sweepTimer: IntervalTimer | null = null;

	function key(accountId: string, client: string) {
		return `${accountId}:${client}`;
	}

	function isSelf(accountId: string, client: string) {
		return accountId === self.accountId && client === self.client;
	}

	function recompute() {
		const accounts = new Set<string>(listen ? [] : [self.accountId]);
		for (const entry of entries.values()) accounts.add(entry.accountId);
		present.value = accounts;
	}

	function sweep() {
		const cutoff = now() - TIME_TO_LIVE;
		let changed = false;
		for (const [k, entry] of entries) {
			if (entry.lastSeen < cutoff) {
				entries.delete(k);
				changed = true;
			}
		}
		if (changed) recompute();
	}

	// A join draws an answer from every other board at once; one timer per
	// quiet window keeps that burst down to a single `here` each.
	function scheduleJoinAnswer() {
		if (listen || joinAnswer) return;
		const delay = JOIN_ANSWER_MIN + Math.random() * (JOIN_ANSWER_MAX - JOIN_ANSWER_MIN);
		joinAnswer = setTimeout(() => {
			joinAnswer = null;
			send({ join: false });
		}, delay);
	}

	function connectedFn() {
		if (connected) return;
		connected = true;
		if (!listen) {
			send({ join: true });
			heartbeat = setInterval(() => send({ join: false }), HEARTBEAT_INTERVAL);
		}
		sweepTimer = setInterval(sweep, SWEEP_INTERVAL);
	}

	function disconnectedFn() {
		connected = false;
		if (heartbeat) clearInterval(heartbeat);
		if (sweepTimer) clearInterval(sweepTimer);
		heartbeat = null;
		sweepTimer = null;
	}

	function received(message: { event: PresenceEvent; accountId: string; client: string }) {
		const { event, accountId, client } = message;
		if (isSelf(accountId, client)) {
			// A stale socket's `leave` can outrun this board's reconnection; answer
			// it at once so nobody's roster drops a board that is still here.
			if (event === "leave" && connected && !listen) send({ join: false });
			return;
		}
		if (event === "leave") {
			if (entries.delete(key(accountId, client))) recompute();
			return;
		}
		entries.set(key(accountId, client), { accountId, client, lastSeen: now() });
		recompute();
		if (event === "join") scheduleJoinAnswer();
	}

	function hiddenFn() {
		hiddenAt = now();
	}

	function visibleFn() {
		const rejoin = hiddenAt != null && now() - hiddenAt >= TIME_TO_LIVE;
		hiddenAt = null;
		if (connected && !listen) send({ join: rejoin });
	}

	function destroy() {
		disconnectedFn();
		if (joinAnswer) clearTimeout(joinAnswer);
		joinAnswer = null;
	}

	return {
		connected: connectedFn,
		disconnected: disconnectedFn,
		received,
		present,
		hidden: hiddenFn,
		visible: visibleFn,
		destroy,
	};
}
